import React, { useEffect, useState } from "react";
import Header from "./Header";
import Footers from "./Footers";
import "./publicPages.css";
import "./PrivacyPolicy.css";

const LAST_UPDATED = "September 30, 2026";

const SECTIONS = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    content: (
      <>
        <h3>Information you provide to us</h3>
        <ul>
          <li>Contact details such as your name, email address, phone number and city.</li>
          <li>Account details such as username and password when you sign up or sign in.</li>
          <li>
            Enrollment details such as the courses you are interested in, your educational
            background and professional experience.
          </li>
          <li>
            Resumes and other information you submit when applying for internships, jobs or
            placement assistance.
          </li>
          <li>
            Payment information when you pay course fees. Payments are processed by third-party
            payment providers and we do not store your full card details.
          </li>
          <li>Any messages, feedback or reviews you send us.</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li>Device and browser information, IP address and operating system.</li>
          <li>Pages you visit, time spent on pages and links you click.</li>
          <li>Cookies and similar technologies, as described in the Cookies section.</li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    content: (
      <ul>
        <li>To register you for courses, deliver training and issue certificates.</li>
        <li>To respond to your enquiries and provide student support.</li>
        <li>To process payments and maintain records of transactions.</li>
        <li>
          To offer internship, job and placement assistance, including sharing your profile with
          prospective employers with your consent.
        </li>
        <li>
          To send you course updates, batch schedules and, where permitted, promotional
          communications. You can opt out at any time.
        </li>
        <li>To improve our website, courses and services.</li>
        <li>To comply with legal obligations and protect against fraud or misuse.</li>
      </ul>
    ),
  },
  {
    id: "how-we-share",
    title: "How We Share Your Information",
    content: (
      <>
        <p>We do not sell your personal information. We may share it only with:</p>
        <ul>
          <li>
            <strong>Trainers and staff</strong> who need it to deliver your course and support you.
          </li>
          <li>
            <strong>Service providers</strong> such as payment gateways, hosting, email and
            communication platforms that work on our behalf under confidentiality obligations.
          </li>
          <li>
            <strong>Employers and hiring partners</strong> when you apply for placements,
            internships or jobs through us.
          </li>
          <li>
            <strong>Authorities</strong> when required by law, court order or to protect our rights,
            users or the public.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "data-retention",
    title: "Data Retention",
    content: (
      <p>
        We keep your personal information only for as long as needed to provide our services,
        maintain your training and certification records, and meet legal, accounting or reporting
        requirements. When it is no longer needed, we securely delete or anonymize it.
      </p>
    ),
  },
  {
    id: "cookies",
    title: "Cookies",
    content: (
      <p>
        Our website uses cookies and similar technologies to remember your preferences, keep you
        signed in and understand how visitors use the site. You can control or delete cookies
        through your browser settings. Disabling cookies may affect some features of the website.
      </p>
    ),
  },
  {
    id: "data-security",
    title: "Data Security",
    content: (
      <p>
        We use reasonable technical and organizational measures to protect your personal
        information against unauthorized access, loss or misuse. However, no method of transmission
        over the internet or electronic storage is completely secure, and we cannot guarantee
        absolute security.
      </p>
    ),
  },
  {
    id: "your-rights",
    title: "Your Rights",
    content: (
      <>
        <p>
          Subject to applicable law, including India&apos;s Digital Personal Data Protection Act,
          2023, you may:
        </p>
        <ul>
          <li>Request access to the personal information we hold about you.</li>
          <li>Ask us to correct or update inaccurate information.</li>
          <li>Ask us to delete your personal information.</li>
          <li>Withdraw your consent to processing or opt out of marketing communications.</li>
          <li>Raise a grievance about how your information is handled.</li>
        </ul>
        <p>To exercise any of these rights, contact us using the details below.</p>
      </>
    ),
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    content: (
      <p>
        Our services are intended for students and professionals. If you are under 18, please use
        our website and services only with the consent and supervision of a parent or guardian. We
        do not knowingly collect personal information from children without such consent.
      </p>
    ),
  },
  {
    id: "third-party-links",
    title: "Third-Party Links",
    content: (
      <p>
        Our website may contain links to third-party websites or platforms. We are not responsible
        for their privacy practices, and we encourage you to read their privacy policies.
      </p>
    ),
  },
  {
    id: "policy-changes",
    title: "Changes to This Policy",
    content: (
      <p>
        We may update this Privacy Policy from time to time. Any changes will be posted on this
        page with a revised &quot;Last updated&quot; date. Please review this page periodically.
      </p>
    ),
  },
  {
    id: "contact-us",
    title: "Contact Us",
    content: (
      <>
        <p>If you have any questions or concerns about this Privacy Policy, please contact us:</p>
        <div className="privacy-contact">
          <p>
            <strong>Open Learnings</strong>
          </p>
          <p>
            #2nd Floor, Plot No. 31/B, Road No. 4, Chaitanya Nagar, B.N. Reddy Nagar, Hyderabad,
            TG, 500 079
          </p>
          <p>
            Email: <a href="mailto:techsupport@openlearnings.online">techsupport@openlearnings.online</a>
          </p>
          <p>
            Phone: <a href="tel:+918142251234">+91 814-225-1234</a>
          </p>
        </div>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    const handleScroll = () => {
      let current = SECTIONS[0].id;
      SECTIONS.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) {
          current = id;
        }
      });
      setActiveId(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const hashId = decodeURIComponent(window.location.hash.slice(1));
    if (hashId) {
      document.getElementById(hashId)?.scrollIntoView();
    }
  }, []);

  const scrollToSection = (event, id) => {
    event.preventDefault();
    window.history.replaceState(window.history.state, "", `#${id}`);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="als-public-page privacy-page d-flex flex-column min-vh-100">
      <Header />

      <div className="als-page-hero">
        <div className="als-page-hero-inner">
          <h1>Privacy Policy</h1>
          <p>How Open Learnings collects, uses and protects your personal information.</p>
        </div>
      </div>

      <section className="privacy-body flex-grow-1">
        <div className="privacy-layout">
          <aside className="privacy-toc">
            <h5>On this page</h5>
            <ol>
              {SECTIONS.map(({ id, title }) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className={activeId === id ? "active" : ""}
                    onClick={(event) => scrollToSection(event, id)}
                  >
                    {title}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <article className="privacy-content">
            <span className="privacy-updated">Last updated: {LAST_UPDATED}</span>
            <p>
              Open Learnings (&quot;we&quot;, &quot;us&quot; or
              &quot;our&quot;) respects your privacy. This Privacy Policy explains how we collect,
              use, share and protect your personal information when you visit our website, enroll
              in our courses, apply for internships or jobs, or otherwise interact with us.
            </p>
            <p>
              By using our website or services, you agree to the practices described in this
              policy. If you do not agree, please do not use our website or services.
            </p>

            {SECTIONS.map(({ id, title, content }, index) => (
              <section key={id} id={id} className="privacy-section">
                <h2>
                  {index + 1}. {title}
                </h2>
                {content}
              </section>
            ))}
          </article>
        </div>
      </section>

      <Footers />
    </div>
  );
}
